import { Divider, Table, Thead, Tbody, Heading } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { EmptyTableRow } from 'components/Table';
import ExportMenu from 'containers/Reports/shared/ExportMenu';
import { ReportsTableDataCell, ReportsTableHeadCell } from 'containers/Reports/shared/ReportsTableCell';
import ReportsTableRow from 'containers/Reports/shared/ReportsTableRow';
import useStickyHeader from 'containers/Reports/shared/useStickyHeader';

interface IReportsByWeekTotalTableProps {
  data: any;
  dateRange: Array<any>;
}

const ReportsByWeekTotalTable: React.FC<IReportsByWeekTotalTableProps> = ({ data, dateRange }) => {
  const { t } = useTranslation(['common', 'reports']);
  useStickyHeader({ tableData: data });

  return (
    <div>
      <Divider my="4" />
      <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
        {t('reports:searchResults')}
      </Heading>
      {data?.attributes ? <ExportMenu attributes={data?.attributes} dateRange={dateRange} /> : null}
      <ScrollSync>
        <div style={{ position: 'relative' }}>
          <Table display="block" id="js-report-table">
            <ScrollSyncPane group="horizontal">
              <Thead
                display="block"
                position="sticky"
                top="0"
                background="primary.50"
                max-width="100%"
                overflow="auto"
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  scrollbarWidth: 'none',
                }}
              >
                <ReportsTableRow color="gray.600" mb="0" border="0">
                  {Array.isArray(data?.headers) && data?.headers?.length ? (
                    <>
                      <ReportsTableHeadCell width="200px">{t('common:name')}</ReportsTableHeadCell>
                      {data?.headers?.map((header: any) => (
                        <ReportsTableHeadCell key={header.day}>
                          {header?.day} <br />
                          {header?.date_formatted}
                        </ReportsTableHeadCell>
                      ))}
                      <ReportsTableHeadCell>{t('common:total')}</ReportsTableHeadCell>
                    </>
                  ) : null}
                </ReportsTableRow>
              </Thead>
            </ScrollSyncPane>
            <ScrollSyncPane group="horizontal">
              <Tbody display="block" max-width="100%" overflow="auto" mt={2}>
                {Array.isArray(data?.data) && data.data.length ? (
                  <>
                    {data.data.map((item: any, index: number) => {
                      return (
                        <ReportsTableRow key={index} minWidth="100%" width="fit-content">
                          {Array.isArray(item?.timesheets) && item.timesheets.length ? (
                            <>
                              <ReportsTableDataCell width="200px">{item?.name}</ReportsTableDataCell>
                              {item.timesheets.map((timesheet: any, childIndex: number) => (
                                <ReportsTableDataCell key={childIndex}>{timesheet}</ReportsTableDataCell>
                              ))}
                            </>
                          ) : null}
                        </ReportsTableRow>
                      );
                    })}
                    {/* Total Data */}
                    <ReportsTableRow bg="primary.50" minWidth="100%" width="fit-content">
                      {Array.isArray(data?.total) && data.total.length ? (
                        <>
                          <ReportsTableDataCell width="200px" fontWeight="semibold">
                            {t('common:total')}
                          </ReportsTableDataCell>
                          {data.total.map((totalItem: any, index: number) => (
                            <ReportsTableDataCell key={index} fontWeight="semibold">
                              {totalItem}
                            </ReportsTableDataCell>
                          ))}
                        </>
                      ) : null}
                    </ReportsTableRow>
                  </>
                ) : (
                  <EmptyTableRow />
                )}
              </Tbody>
            </ScrollSyncPane>
          </Table>
        </div>
      </ScrollSync>
    </div>
  );
};

export default ReportsByWeekTotalTable;
